import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import Skeleton from 'react-loading-skeleton';
import {useSelector} from 'react-redux';
import {useRouteMatch} from 'react-router-dom';
import {selectEventOptionEnablePhotoSharing} from '~/common/fabric/reducers/eviteEvent/selectors';
import {selectPhotosEnabled} from '~/common/fabric/reducers/giftingOptions/selectors';
import {useLazyGetPhotosQuery} from '~/ViewInvitation/api/endpoints/photos';
import {useImageProxySizer} from '~/ViewInvitation/hooks/useImageProxySizer';
import {usePhotos} from '~/ViewInvitation/hooks/usePhotos';
import {usePostPhoto} from '~/ViewInvitation/hooks/usePostPhoto';
import {PHOTO_STATUS} from '~/ViewInvitation/reducers/constants';
import {PhotoUploadButton} from '~/ViewInvitation/routes/Landing/PhotosModule/PhotoUploadButton';
import {PhotoGallery} from '~/ViewInvitation/routes/Photos/PhotoGallery/PhotoGallery';
import {PhotoGalleryEditor} from '~/ViewInvitation/routes/Photos/PhotoGalleryEditor/PhotoGalleryEditor';
import {PhotoGalleryUploadInput} from '~/ViewInvitation/routes/Photos/PhotoGalleryUploadInput';
import {PhotosModuleTile} from '~/ViewInvitation/routes/Photos/PhotosModuleTile/PhotosModuleTile';
import {PhotoViewer} from '~/ViewInvitation/routes/Photos/PhotoViewer/PhotoViewer';
import {selectEventIsFutureNonFuneral} from '~/ViewInvitation/selectors/eventDetails';
import {eventHasPassed, isPreview} from '~/ViewInvitation/utils';
import {Button} from '~library/atoms/Button';
import {Typography} from '~library/atoms/Typography';
import {PhotoGalleryIcon} from '~library/svg/PhotoGalleryIcon';
import {CameraIcon} from '~library/svg/v2/CameraIcon';
// import {selectEventType} from '~/common/fabric/reducers/eviteEvent/selectors'; // coming soon for category-based upsells

import {charcoal, white} from '~libSass/base/_exports.sass';
import 'react-loading-skeleton/dist/skeleton.css';
import './PhotosModule.sass';

export const PHOTO_MODULE_PER_PAGE = 3;

const loadingState = (
  <>
    <Skeleton className="photos__loading-skeleton" />
    <Skeleton className="photos__loading-skeleton" />
    <Skeleton className="photos__loading-skeleton" />
  </>
);

export const PhotosModule = ({
  isCreationFlow,
  openPhotoGallery,
  openPhotoViewer,
  openPhotoGalleryEdit,
}) => {
  const {getThumbnailUrl} = useImageProxySizer();
  const photosEnabledCreateFlow = useSelector(selectPhotosEnabled);
  const photosEnabledNotCreateFlow = useSelector(selectEventOptionEnablePhotoSharing);
  const photosEnabled = photosEnabledCreateFlow || photosEnabledNotCreateFlow;
  const [getPhotos] = useLazyGetPhotosQuery();
  const hasRsvpd = useSelector((state) => state.rsvp?.hasRsvpd ?? false);
  const rsvpOff = useSelector((state) => state.eventDetails.rsvpOff);
  const eventId = useSelector((state) => state.eventDetails?.eventId);
  const stagedPhotos = useSelector((state) => state.photos.photos);
  const eventIsFutureNonFuneral = useSelector(selectEventIsFutureNonFuneral);
  const [isPosting, setIsPosting] = useState(false);
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(openPhotoGallery);
  const [galleryEditorOpen, setGalleryEditorOpen] = useState(openPhotoGalleryEdit);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(openPhotoViewer);
  const [photoViewerId, setPhotoViewerId] = useState(null);
  const [galleryFocusTrapPaused, setGalleryFocusTrapPaused] = useState(false);
  const searchParams = new URLSearchParams(window.location.search);
  const isCreateEditPreview = !!isPreview();
  const isGuestPreview = !!searchParams.get('preview');
  const isOnPhotoGalleryRoute = useRouteMatch('/photo-gallery');
  const isOnPhotoRoute = useRouteMatch('/photo');
  const isOnPhotosRoute = useRouteMatch('/photos');
  const unlockUI = eventHasPassed() || hasRsvpd || rsvpOff;
  const fileUploadRef = useRef();

  const pendingPhotos = stagedPhotos?.filter((p) => p.status === PHOTO_STATUS.PENDING);

  const {doPostPhotos} = usePostPhoto({setIsPosting, isPosting});

  const {photos, isLoading: isLoadingPhotos} = usePhotos({
    eventId,
    isHostModule: false,
    perPage: PHOTO_MODULE_PER_PAGE,
  });
  const isLoading = isLoadingPhotos || isPosting;
  const showComingSoonState = photos?.length === 0 && eventIsFutureNonFuneral;

  const handlePost = useCallback(() => {
    setGalleryEditorOpen(false);
    doPostPhotos(pendingPhotos);
  }, [pendingPhotos, doPostPhotos]);

  const onViewGallery = useCallback(() => {
    setPhotoGalleryOpen(true);
  }, []);

  const onEditGallery = useCallback(() => {
    setGalleryEditorOpen(true);
    setPhotoGalleryOpen(false);
  }, []);

  const onViewPhotoFn = useCallback(
    (photoId) => () => {
      setPhotoViewerId(photoId);
      setPhotoViewerOpen(true);
      setGalleryFocusTrapPaused(true);
    },
    []
  );

  const onClosePhotoViewer = useCallback(() => {
    setGalleryFocusTrapPaused(false);
    setPhotoViewerOpen(false);
  }, []);

  const viewAllButton = useMemo(
    () => (
      <Button
        className="photos__view-gallery-button"
        variant="transparent"
        data-qa-id="view-gallery-button"
        onClick={onViewGallery}
        title="Click to view gallery"
      >
        <PhotoGalleryIcon />
        <Typography variant="paragraph1">View All</Typography>
      </Button>
    ),
    [onViewGallery]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (getPhotos && eventId) {
      const timeoutLength = isOnPhotosRoute || isOnPhotoGalleryRoute ? 0 : 3000;
      if (!isOnPhotoRoute) {
        const timeout = setTimeout(() => {
          getPhotos({
            eventId,
            perPage: -1,
            includeCount: true,
          });
        }, timeoutLength);
        return () => {
          clearTimeout(timeout);
        };
      }
    }
    return undefined;
  }, [eventId, getPhotos]);

  if (!photosEnabled && (!isCreateEditPreview || isGuestPreview)) {
    return null;
  }

  const moduleMessaging = () => {
    let message = '';
    let color = 'pebble';
    if (isCreationFlow) {
      message = 'Photos will appear here!';
    } else if (!isCreationFlow && !isLoading && !unlockUI) {
      message = 'RSVP to view and share photos for this event.';
    } else if (!isLoading && unlockUI && photos.length === 0) {
      color = 'charcoal';
      message = 'No photos yet - be the first to share!';
    }

    return (
      message && (
        <Typography
          variant="paragraph3"
          data-qa-id="no-photos"
          color={color}
          className="photos__no-rsvp-message"
        >
          {message}
        </Typography>
      )
    );
  };

  return (
    <>
      <div className="photos__container">
        <div className="photos__header">
          <Typography variant="label2" data-qa-id="photos-module-title" color="pebble">
            Shared album
          </Typography>
          {!showComingSoonState && (
            <PhotoUploadButton
              disabled={isCreationFlow}
              handlePost={handlePost}
              fileUploadRef={fileUploadRef}
              iconColor={isCreationFlow ? charcoal : white}
            >
              Add photos
            </PhotoUploadButton>
          )}
        </div>
        {showComingSoonState
          ? <div className="photos__not-yet-available">
              <CameraIcon />
              <Typography variant="paragraph1" semibold>
                Coming soon!
              </Typography>
              <Typography variant="paragraph2">
                Check back here when the event starts to add photos.
              </Typography>
            </div>
          : moduleMessaging()}
        <div className="photos__carousel" data-qa-id="photos-carousel">
          {isLoading && unlockUI && !showComingSoonState && loadingState}
          {!isLoading &&
            unlockUI &&
            photos.length > 0 &&
            photos.map((photo, i) => (
              <PhotosModuleTile
                key={photo.id}
                idx={i}
                id={photo.id}
                url={photo.content.url}
                length={photos.length}
                getThumbnailUrl={getThumbnailUrl}
                onViewGallery={onViewGallery}
                onViewPhotoFn={onViewPhotoFn}
                hasRadius
                isViewAll={i === 2}
              />
            ))}
          {!isLoading && unlockUI && photos.length < 3 && photos.length > 0 && viewAllButton}
        </div>
      </div>
      {galleryEditorOpen && (
        <PhotoGalleryEditor
          isOpen={galleryEditorOpen}
          onClose={() => setGalleryEditorOpen(false)}
          stagedPhotos={stagedPhotos}
          handlePost={handlePost}
          onViewPhotoFn={onViewPhotoFn}
          setPhotoGalleryOpen={setPhotoGalleryOpen}
          onEditGallery={onEditGallery}
        />
      )}
      {photoGalleryOpen && (
        <PhotoGallery
          isOpen={photoGalleryOpen}
          onClose={() => setPhotoGalleryOpen(false)}
          onEditGallery={onEditGallery}
          handlePost={handlePost}
          setPhotoGalleryOpen={setPhotoGalleryOpen}
          onViewPhotoFn={onViewPhotoFn}
          focusTrapPaused={galleryFocusTrapPaused}
          showComingSoonState={showComingSoonState}
        />
      )}
      {photoViewerOpen && (
        <PhotoViewer initialPhotoId={photoViewerId} onClose={onClosePhotoViewer} />
      )}
      <PhotoGalleryUploadInput
        fileUploadRef={fileUploadRef}
        collection="photos"
        setPhotoGalleryOpen={setPhotoGalleryOpen}
        onEditGallery={onEditGallery}
      />
    </>
  );
};
